import { z } from "zod/v4";

/**
 * Simple Zod validation composable for reactive values.
 *
 * @param schema - The Zod schema to validate against
 * @param valueRef - A ref containing the value to validate
 * @returns An object with:
 *   - `isValid`: A computed boolean indicating if the value is valid
 *   - `errors`: A computed containing Zod validation errors (undefined if valid)
 *
 * @example
 * ```ts
 * const name = ref('');
 * const nameSchema = z.string().min(1);
 * const { isValid, errors } = useZodValidation(nameSchema, name);
 * ```
 */
export function useZodValidation<T>(schema: z.ZodType<T>, valueRef: Ref<T>) {
  const result = computed(() => schema.safeParse(valueRef.value));

  const isValid = computed(() => result.value.success);

  const errors = computed(() => {
    if (result.value.success) return undefined;
    return z.treeifyError(result.value.error);
  });

  return {
    isValid,
    errors,
  };
}
